<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import AutomationEventsAPI from 'dashboard/api/automationEvents';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  event: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  // Quem pode pedir nova tentativa de etapa (a API exige administrador).
  canRetry: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'retried']);
const { t } = useI18n();
const { accountScopedUrl } = useAccount();

const activeTab = ref('summary');
const headingRef = ref(null);

// Nova tentativa de etapa: o comando vai para a fila e o resultado aparece no
// próprio passo, sem precisar sair da tela nem chamar a API na mão.
const RETRYABLE_STEP_STATUSES = ['failed', 'manual_action'];
const TERMINAL_COMMAND_STATUSES = ['succeeded', 'failed', 'cancelled'];
const RETRY_POLL_INTERVAL_MS = 3000;
const RETRY_POLL_MAX_ATTEMPTS = 20;

const confirmingStepId = ref(null);
const retryingStepId = ref(null);
const retryStates = ref({});
const retryTimers = new Map();

const clearRetryTimers = () => {
  retryTimers.forEach(timer => clearTimeout(timer));
  retryTimers.clear();
};

const resetRetryState = () => {
  clearRetryTimers();
  confirmingStepId.value = null;
  retryingStepId.value = null;
  retryStates.value = {};
};

watch(
  () => props.event?.id,
  () => {
    activeTab.value = 'summary';
    resetRetryState();
  }
);

onBeforeUnmount(clearRetryTimers);

const setRetryState = (stepId, state) => {
  retryStates.value = { ...retryStates.value, [stepId]: state };
};

const isRetryActive = stepId => {
  const state = retryStates.value[stepId];
  return Boolean(
    state &&
      !state.error &&
      !TERMINAL_COMMAND_STATUSES.includes(state.commandStatus)
  );
};

const canRetryStep = step =>
  props.canRetry &&
  RETRYABLE_STEP_STATUSES.includes(step.status) &&
  !isRetryActive(step.id);

const pollRetryCommand = (stepId, commandId, eventId, attempt = 1) => {
  const timer = setTimeout(async () => {
    retryTimers.delete(stepId);
    if (props.event?.id !== eventId) return;
    try {
      const { data } = await AutomationEventsAPI.showCommand(commandId);
      if (props.event?.id !== eventId) return;
      const commandStatus = data?.automation_step_command?.status;
      setRetryState(stepId, { commandId, commandStatus });
      if (TERMINAL_COMMAND_STATUSES.includes(commandStatus)) {
        emit('retried', eventId);
        return;
      }
    } catch {
      // Falha de leitura não cancela a tentativa: segue consultando até o teto.
    }
    if (attempt < RETRY_POLL_MAX_ATTEMPTS) {
      pollRetryCommand(stepId, commandId, eventId, attempt + 1);
    }
  }, RETRY_POLL_INTERVAL_MS);
  retryTimers.set(stepId, timer);
};

const confirmRetry = async step => {
  const eventId = props.event?.id;
  retryingStepId.value = step.id;
  try {
    const { data } = await AutomationEventsAPI.retryStep(step.id);
    if (props.event?.id !== eventId) return;
    const command = data?.automation_step_command || {};
    setRetryState(step.id, {
      commandId: command.command_id,
      commandStatus: command.status || 'pending',
    });
    emit('retried', eventId);
    if (command.command_id)
      pollRetryCommand(step.id, command.command_id, eventId);
  } catch {
    if (props.event?.id !== eventId) return;
    setRetryState(step.id, { error: true });
  } finally {
    if (retryingStepId.value === step.id) retryingStepId.value = null;
    if (confirmingStepId.value === step.id) confirmingStepId.value = null;
  }
};

const commandStatusLabel = status => {
  const key = `AUTOMATION_EVENTS.COMMAND_STATUSES.${String(status || 'pending').toUpperCase()}`;
  const label = t(key);
  return label === key ? status : label;
};

const retryMessage = stepId => {
  const state = retryStates.value[stepId];
  if (!state) return '';
  if (state.error) return t('AUTOMATION_EVENTS.RETRY.ERROR');
  if (state.commandStatus === 'pending')
    return t('AUTOMATION_EVENTS.RETRY.QUEUED');
  return t('AUTOMATION_EVENTS.RETRY.RESULT', {
    status: commandStatusLabel(state.commandStatus),
  });
};

const retryMessageClass = stepId => {
  const state = retryStates.value[stepId];
  if (state?.error || state?.commandStatus === 'failed')
    return 'text-n-ruby-11';
  if (state?.commandStatus === 'succeeded') return 'text-n-teal-11';
  return 'text-n-slate-11';
};

const focus = () => headingRef.value?.focus();
defineExpose({ focus });

const formatDate = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');

  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const payloadJson = payload => JSON.stringify(payload || {}, null, 2);
const hasOwn = (record, key) =>
  Object.prototype.hasOwnProperty.call(record || {}, key);
const hasEventPayloads = computed(
  () =>
    hasOwn(props.event, 'raw_payload') ||
    hasOwn(props.event, 'normalized_payload')
);
const hasStepPayloads = step =>
  hasOwn(step, 'request_payload') || hasOwn(step, 'response_payload');

const statusLabel = status => {
  switch (status) {
    case 'completed':
      return t('AUTOMATION_EVENTS.STATUSES.COMPLETED');
    case 'failed':
      return t('AUTOMATION_EVENTS.STATUSES.FAILED');
    case 'ignored':
      return t('AUTOMATION_EVENTS.STATUSES.IGNORED');
    case 'manual_action':
      return t('AUTOMATION_EVENTS.STATUSES.MANUAL_ACTION');
    case 'pending':
      return t('AUTOMATION_EVENTS.STATUSES.PENDING');
    case 'processing':
      return t('AUTOMATION_EVENTS.STATUSES.PROCESSING');
    case 'received':
      return t('AUTOMATION_EVENTS.STATUSES.RECEIVED');
    case 'running':
      return t('AUTOMATION_EVENTS.STATUSES.RUNNING');
    case 'skipped':
      return t('AUTOMATION_EVENTS.STATUSES.SKIPPED');
    case 'success':
      return t('AUTOMATION_EVENTS.STATUSES.SUCCESS');
    default:
      return status;
  }
};

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
    success: 'bg-n-teal-3 text-n-teal-11',
    running: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
    skipped: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const eventSubtitle = computed(() => {
  if (!props.event) return '';

  return `#${props.event.id} · ${props.event.source} · ${props.event.event_type}`;
});

const stepMeta = step =>
  t('AUTOMATION_EVENTS.DETAIL.STEP_META', {
    stepKey: step.step_key,
    attempts: step.attempt_count,
  });

const contactUrl = computed(() => {
  if (!props.event?.contact_id) return null;
  return accountScopedUrl(`contacts/${props.event.contact_id}`);
});

const conversationUrl = computed(() => {
  const displayId = props.event?.conversation_display_id;
  if (!displayId) return null;
  return accountScopedUrl(`conversations/${displayId}`);
});

const summaryRows = computed(() => {
  if (!props.event) return [];

  return [
    [t('AUTOMATION_EVENTS.FIELDS.SOURCE'), props.event.source],
    [t('AUTOMATION_EVENTS.FIELDS.EVENT_TYPE'), props.event.event_type],
    [t('AUTOMATION_EVENTS.FIELDS.LAUNCH'), props.event.launch_id],
    [t('AUTOMATION_EVENTS.FIELDS.STATUS'), statusLabel(props.event.status)],
    [t('AUTOMATION_EVENTS.FIELDS.TRANSACTION'), props.event.transaction_id],
    [
      t('AUTOMATION_EVENTS.FIELDS.PRODUCT'),
      props.event.product_name || props.event.product_code,
    ],
    [t('AUTOMATION_EVENTS.FIELDS.OFFER'), props.event.offer_code],
    [t('AUTOMATION_EVENTS.FIELDS.CUSTOMER'), props.event.customer_name],
    [t('AUTOMATION_EVENTS.FIELDS.EMAIL'), props.event.customer_email],
    [t('AUTOMATION_EVENTS.FIELDS.PHONE'), props.event.customer_phone],
    [
      t('AUTOMATION_EVENTS.FIELDS.RECEIVED_AT'),
      formatDate(props.event.received_at),
    ],
    [
      t('AUTOMATION_EVENTS.FIELDS.PROCESSED_AT'),
      formatDate(props.event.processed_at),
    ],
  ].filter(([, value]) => value);
});

// Identificadores técnicos ficam na aba Dados técnicos: o resumo fala a
// língua de quem abre a tela para saber por que a compra não entrou.
const technicalRows = computed(() => {
  if (!props.event) return [];

  return [
    [
      t('AUTOMATION_EVENTS.FIELDS.EXTERNAL_EVENT_ID'),
      props.event.external_event_id,
    ],
    [
      t('AUTOMATION_EVENTS.FIELDS.IDEMPOTENCY_KEY'),
      props.event.idempotency_key,
    ],
  ].filter(([, value]) => value);
});

const attentionStep = computed(() => {
  const status = props.event?.status;
  const steps = props.event?.steps || [];
  const fromSteps = steps.find(step => step.status === status);
  if (fromSteps) return fromSteps;
  if (status === 'failed') return props.event?.steps_summary?.failed?.[0];
  return null;
});

const whatHappened = computed(() => {
  const status = props.event?.status;
  const step = attentionStep.value;
  const stepName = step?.step_label || step?.step_key;
  switch (status) {
    case 'completed':
      return t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.COMPLETED');
    case 'processing':
      return t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.PROCESSING');
    case 'received':
      return t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.RECEIVED');
    case 'ignored':
      return t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.IGNORED');
    case 'failed':
      return stepName
        ? t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.FAILED_STEP', { step: stepName })
        : t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.FAILED');
    case 'manual_action':
      return stepName
        ? t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.MANUAL_ACTION_STEP', {
            step: stepName,
          })
        : t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.MANUAL_ACTION');
    default:
      return '';
  }
});

const showRetryHint = computed(
  () =>
    props.canRetry &&
    (props.event?.steps || []).some(step =>
      RETRYABLE_STEP_STATUSES.includes(step.status)
    )
);

const tabs = computed(() => [
  { id: 'summary', label: t('AUTOMATION_EVENTS.DETAIL.TABS.SUMMARY') },
  { id: 'steps', label: t('AUTOMATION_EVENTS.DETAIL.TABS.STEPS') },
  { id: 'technical', label: t('AUTOMATION_EVENTS.DETAIL.TABS.TECHNICAL') },
]);
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col bg-n-solid-1">
    <header
      class="flex shrink-0 items-start justify-between gap-3 border-b border-n-weak px-4 py-3"
    >
      <div class="min-w-0">
        <h2
          ref="headingRef"
          tabindex="-1"
          class="truncate text-base font-semibold text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        >
          {{ t('AUTOMATION_EVENTS.DETAIL.TITLE') }}
        </h2>
        <p v-if="event" class="mt-0.5 truncate text-xs text-n-slate-11">
          {{ eventSubtitle }}
        </p>
      </div>
      <button
        v-if="compact"
        type="button"
        class="flex size-11 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 lg:size-8"
        :aria-label="t('AUTOMATION_EVENTS.ACTIONS.CLOSE')"
        @click="emit('close')"
      >
        <span class="i-ri-close-line size-4" />
      </button>
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center gap-2 text-sm text-n-slate-11"
    >
      <span class="i-ri-loader-2-line size-4 animate-spin" />
      {{ t('AUTOMATION_EVENTS.LOADING') }}
    </div>

    <div
      v-else-if="!event"
      class="flex flex-1 items-center justify-center px-6 text-center text-sm text-n-slate-11"
    >
      {{ t('AUTOMATION_EVENTS.DETAIL.EMPTY_SELECTION') }}
    </div>

    <template v-else>
      <div
        v-if="contactUrl || conversationUrl"
        class="flex shrink-0 flex-wrap gap-2 border-b border-n-weak px-4 py-2"
      >
        <a
          v-if="contactUrl"
          :href="contactUrl"
          class="inline-flex h-8 min-h-11 items-center gap-1.5 rounded-md bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3 lg:min-h-0"
        >
          <span class="i-ri-user-line size-3.5" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.OPEN_CONTACT') }}
        </a>
        <a
          v-if="conversationUrl"
          :href="conversationUrl"
          class="inline-flex h-8 min-h-11 items-center gap-1.5 rounded-md bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3 lg:min-h-0"
        >
          <span class="i-ri-message-2-line size-3.5" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.OPEN_CONVERSATION') }}
        </a>
      </div>

      <nav
        class="flex shrink-0 gap-1 border-b border-n-weak px-3 pt-2"
        role="tablist"
      >
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          class="min-h-11 rounded-t-md px-3 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand lg:min-h-0"
          :class="
            activeTab === tab.id
              ? 'border-b-2 border-n-brand text-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :aria-selected="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="min-h-0 flex-1 overflow-y-auto p-4">
        <section v-if="activeTab === 'summary'" class="space-y-3">
          <div v-if="event.status" class="flex items-center gap-2">
            <span
              class="rounded px-2 py-0.5 text-xs font-medium"
              :class="statusClass(event.status)"
            >
              {{ statusLabel(event.status) }}
            </span>
          </div>

          <div
            v-if="whatHappened"
            class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
          >
            <div class="text-[11px] font-medium uppercase text-n-slate-10">
              {{ t('AUTOMATION_EVENTS.DETAIL.WHAT_HAPPENED') }}
            </div>
            <p class="mt-0.5 text-sm text-n-slate-12">
              {{ whatHappened }}
            </p>
            <p v-if="showRetryHint" class="mt-1 text-xs text-n-slate-11">
              {{ t('AUTOMATION_EVENTS.DETAIL.EXPLAIN.RETRY_HINT') }}
            </p>
          </div>

          <div
            v-if="event.current_error"
            class="rounded-md border border-n-ruby-5 bg-n-ruby-2 px-3 py-2"
          >
            <div class="text-[11px] font-medium uppercase text-n-ruby-11">
              {{ t('AUTOMATION_EVENTS.FIELDS.CURRENT_ERROR') }}
            </div>
            <div class="mt-1 break-words text-sm text-n-ruby-12">
              {{ event.current_error }}
            </div>
          </div>

          <div class="grid gap-2 sm:grid-cols-2">
            <div
              v-for="[label, value] in summaryRows"
              :key="label"
              class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
            >
              <div class="text-[11px] font-medium uppercase text-n-slate-10">
                {{ label }}
              </div>
              <div class="mt-0.5 break-words text-sm text-n-slate-12">
                {{ value }}
              </div>
            </div>
          </div>
        </section>

        <section v-else-if="activeTab === 'steps'">
          <div
            v-if="!event.steps?.length"
            class="rounded-md border border-n-weak px-3 py-8 text-center text-sm text-n-slate-11"
          >
            {{ t('AUTOMATION_EVENTS.DETAIL.NO_STEPS') }}
          </div>
          <div
            v-else
            class="relative ml-2 space-y-2 border-l border-n-weak pl-4"
          >
            <div
              v-for="step in event.steps"
              :key="step.id"
              class="relative rounded-md border border-n-weak bg-n-solid-2 px-3 py-3"
            >
              <span
                class="i-ri-git-branch-line absolute -left-[1.35rem] top-3 size-4 bg-n-solid-1 text-n-slate-10"
              />
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div class="min-w-0">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ step.step_label || step.step_key }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ stepMeta(step) }}
                  </div>
                </div>
                <span
                  class="rounded px-2 py-0.5 text-xs font-medium"
                  :class="statusClass(step.status)"
                >
                  {{ statusLabel(step.status) }}
                </span>
              </div>
              <p
                v-if="step.error_message"
                class="mt-2 break-words text-xs text-n-ruby-11"
              >
                {{ step.error_message }}
              </p>
              <div
                v-if="retryStates[step.id] || canRetryStep(step)"
                class="mt-2 flex flex-wrap items-center gap-2"
                :data-testid="`automation-step-retry-${step.id}`"
              >
                <template v-if="confirmingStepId === step.id">
                  <span class="text-xs text-n-slate-12">
                    {{ t('AUTOMATION_EVENTS.RETRY.CONFIRM') }}
                  </span>
                  <Button
                    size="sm"
                    class="min-h-11 lg:min-h-0"
                    :label="t('AUTOMATION_EVENTS.RETRY.CONFIRM_YES')"
                    :is-loading="retryingStepId === step.id"
                    data-testid="automation-step-retry-confirm"
                    @click="confirmRetry(step)"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="slate"
                    class="min-h-11 lg:min-h-0"
                    :label="t('AUTOMATION_EVENTS.RETRY.CANCEL')"
                    :disabled="retryingStepId === step.id"
                    @click="confirmingStepId = null"
                  />
                </template>
                <Button
                  v-else-if="canRetryStep(step)"
                  size="sm"
                  variant="faded"
                  color="slate"
                  icon="i-ri-restart-line"
                  class="min-h-11 lg:min-h-0"
                  :label="t('AUTOMATION_EVENTS.RETRY.BUTTON')"
                  data-testid="automation-step-retry-button"
                  @click="confirmingStepId = step.id"
                />
                <p
                  v-if="retryStates[step.id]"
                  role="status"
                  class="text-xs"
                  :class="retryMessageClass(step.id)"
                >
                  {{ retryMessage(step.id) }}
                </p>
              </div>
              <div
                v-if="hasStepPayloads(step)"
                class="mt-2 grid gap-2 md:grid-cols-2"
              >
                <textarea
                  readonly
                  class="h-40 resize-none overflow-auto rounded bg-n-alpha-2 p-2 font-mono text-[11px] text-n-slate-11"
                  :value="payloadJson(step.request_payload)"
                />
                <textarea
                  readonly
                  class="h-40 resize-none overflow-auto rounded bg-n-alpha-2 p-2 font-mono text-[11px] text-n-slate-11"
                  :value="payloadJson(step.response_payload)"
                />
              </div>
            </div>
          </div>
        </section>

        <section v-else class="space-y-4">
          <div v-if="technicalRows.length">
            <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
              {{ t('AUTOMATION_EVENTS.DETAIL.IDENTIFIERS') }}
            </h3>
            <div class="grid gap-2 sm:grid-cols-2">
              <div
                v-for="[label, value] in technicalRows"
                :key="label"
                class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
              >
                <div class="text-[11px] font-medium uppercase text-n-slate-10">
                  {{ label }}
                </div>
                <div class="mt-0.5 break-all font-mono text-xs text-n-slate-12">
                  {{ value }}
                </div>
              </div>
            </div>
          </div>
          <template v-if="hasEventPayloads">
            <div>
              <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ t('AUTOMATION_EVENTS.DETAIL.RAW_PAYLOAD') }}
              </h3>
              <textarea
                readonly
                class="h-56 w-full resize-none overflow-auto rounded-md border border-n-weak bg-n-alpha-2 p-3 font-mono text-xs text-n-slate-11"
                :value="payloadJson(event.raw_payload)"
              />
            </div>
            <div>
              <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ t('AUTOMATION_EVENTS.DETAIL.NORMALIZED_PAYLOAD') }}
              </h3>
              <textarea
                readonly
                class="h-56 w-full resize-none overflow-auto rounded-md border border-n-weak bg-n-alpha-2 p-3 font-mono text-xs text-n-slate-11"
                :value="payloadJson(event.normalized_payload)"
              />
            </div>
          </template>
          <div
            v-else
            class="rounded-md border border-n-weak px-3 py-8 text-center text-sm text-n-slate-11"
          >
            {{
              event
                ? t('AUTOMATION_EVENTS.DETAIL.TECHNICAL_ADMIN_ONLY')
                : t('AUTOMATION_EVENTS.DETAIL.NO_TECHNICAL')
            }}
          </div>
        </section>
      </div>
    </template>
  </div>
</template>
